import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { EPN_CONFIG } from '../config/epn';

// Goat Cards API configuration
const GOAT_CARDS_API_BASE_URL = 'https://api.goatcards.shop';

// Types for Goat Cards API responses
export interface GoatCard {
  id: string;
  title: string;
  player: string;
  team: string;
  year: number;
  brand: string;
  condition: string;
  grade?: string;
  gradingCompany?: string;
  price: number;
  priceChange?: number;
  isHot?: boolean;
  imageUrl: string;
  description: string;
  ebayUrl?: string;
  sport: 'basketball' | 'football' | 'baseball';
  isGraded: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GoatCardCreateRequest {
  title: string;
  player: string;
  team: string;
  year: number;
  brand: string;
  condition: string;
  grade?: string;
  gradingCompany?: string;
  price: number;
  imageUrl: string;
  description: string;
  ebayUrl?: string;
  sport: 'basketball' | 'football' | 'baseball';
  isGraded: boolean;
}

export interface GoatCardUpdateRequest extends Partial<GoatCardCreateRequest> {
  id: string;
}

export interface GoatCardsApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface GoatCardsPaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message?: string;
  error?: string;
}

export interface GoatCardsSearchFilters {
  search?: string;
  sport?: 'basketball' | 'football' | 'baseball';
  player?: string;
  team?: string;
  yearMin?: number;
  yearMax?: number;
  brand?: string;
  condition?: string;
  grade?: string;
  priceMin?: number;
  priceMax?: number;
  isGraded?: boolean;
  isHot?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'price' | 'year' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

// Goat Cards API Service Class
class GoatCardsApiService {
  private api: AxiosInstance;



  // Generate EPN tracking URL for eBay listings
  generateEbayTrackingUrl(ebayItemUrl: string): string {
    if (!EPN_CONFIG.CAMPAIGN_ID || EPN_CONFIG.CAMPAIGN_ID === 'YOUR_CAMPAIGN_ID') {
      console.warn('[EPN] Campaign ID not configured. Using original eBay URL.');
      return ebayItemUrl;
    }

    try {
      const trackingUrl = new URL(EPN_CONFIG.TRACKING_URL);
      
      // Add EPN parameters
      trackingUrl.searchParams.set('campid', EPN_CONFIG.CAMPAIGN_ID);
      trackingUrl.searchParams.set('toolid', EPN_CONFIG.TOOL_ID);
      trackingUrl.searchParams.set('customid', EPN_CONFIG.CUSTOM_ID);
      
      // Add the original eBay item URL
      trackingUrl.searchParams.set('mpre', ebayItemUrl);
      
      return trackingUrl.toString();
    } catch (error) {
      console.error('[EPN] Failed to generate tracking URL:', error);
      return ebayItemUrl; // Fallback to original URL
    }
  }

  constructor() {
    this.api = axios.create({
      baseURL: GOAT_CARDS_API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for logging
    this.api.interceptors.request.use(
      (config) => {
        console.log(`[GoatCards API] ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('[GoatCards API] Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('[GoatCards API] Response error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.api.get('/health');
      return response.status === 200;
    } catch (error) {
      console.error('[GoatCards API] Health check failed:', error);
      return false;
    }
  }

  // Get API documentation
  async getDocumentation(): Promise<string> {
    try {
      const response = await this.api.get('/documentation/static/index.html');
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to get documentation:', error);
      throw error;
    }
  }

  // Cards CRUD operations
  async getAllCards(filters?: GoatCardsSearchFilters): Promise<GoatCardsPaginatedResponse<GoatCard>> {
    try {
      const response: AxiosResponse<GoatCardsPaginatedResponse<GoatCard>> = await this.api.get('/cards', {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to get all cards:', error);
      throw error;
    }
  }

  async getCardById(id: string): Promise<GoatCardsApiResponse<GoatCard>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<GoatCard>> = await this.api.get(`/cards/${id}`);
      return response.data;
    } catch (error) {
      console.error(`[GoatCards API] Failed to get card ${id}:`, error);
      throw error;
    }
  }

  async createCard(cardData: GoatCardCreateRequest): Promise<GoatCardsApiResponse<GoatCard>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<GoatCard>> = await this.api.post('/cards', cardData);
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to create card:', error);
      throw error;
    }
  }

  async updateCard(cardData: GoatCardUpdateRequest): Promise<GoatCardsApiResponse<GoatCard>> {
    try {
      const { id, ...updateData } = cardData;
      const response: AxiosResponse<GoatCardsApiResponse<GoatCard>> = await this.api.put(`/cards/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error(`[GoatCards API] Failed to update card ${cardData.id}:`, error);
      throw error;
    }
  }

  async deleteCard(id: string): Promise<GoatCardsApiResponse<void>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<void>> = await this.api.delete(`/cards/${id}`);
      return response.data;
    } catch (error) {
      console.error(`[GoatCards API] Failed to delete card ${id}:`, error);
      throw error;
    }
  }

  // Search and filter operations
  async searchCards(searchTerm: string, filters?: Omit<GoatCardsSearchFilters, 'search'>): Promise<GoatCardsPaginatedResponse<GoatCard>> {
    try {
      const response: AxiosResponse<GoatCardsPaginatedResponse<GoatCard>> = await this.api.get('/cards/search', {
        params: { search: searchTerm, ...filters },
      });
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to search cards:', error);
      throw error;
    }
  }

  // eBay search endpoint
  async searchEbay(searchTerm: string, limit: number = 45, offset: number = 0): Promise<GoatCardsApiResponse<any>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<any>> = await this.api.get('/api/v1/ebay/search', {
        params: { q: searchTerm, limit, offset },
      });
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to search eBay:', error);
      throw error;
    }
  }

  async getHotCards(limit: number = 10): Promise<GoatCardsApiResponse<GoatCard[]>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<GoatCard[]>> = await this.api.get('/cards/hot', {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to get hot cards:', error);
      throw error;
    }
  }

  async getCardsByPlayer(player: string, filters?: Omit<GoatCardsSearchFilters, 'player'>): Promise<GoatCardsPaginatedResponse<GoatCard>> {
    try {
      const response: AxiosResponse<GoatCardsPaginatedResponse<GoatCard>> = await this.api.get(`/cards/player/${encodeURIComponent(player)}`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error(`[GoatCards API] Failed to get cards for player ${player}:`, error);
      throw error;
    }
  }

  async getCardsByTeam(team: string, filters?: Omit<GoatCardsSearchFilters, 'team'>): Promise<GoatCardsPaginatedResponse<GoatCard>> {
    try {
      const response: AxiosResponse<GoatCardsPaginatedResponse<GoatCard>> = await this.api.get(`/cards/team/${encodeURIComponent(team)}`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error(`[GoatCards API] Failed to get cards for team ${team}:`, error);
      throw error;
    }
  }

  async getCardsBySport(sport: 'basketball' | 'football' | 'baseball', filters?: Omit<GoatCardsSearchFilters, 'sport'>): Promise<GoatCardsPaginatedResponse<GoatCard>> {
    try {
      const response: AxiosResponse<GoatCardsPaginatedResponse<GoatCard>> = await this.api.get(`/cards/sport/${sport}`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error(`[GoatCards API] Failed to get cards for sport ${sport}:`, error);
      throw error;
    }
  }

  // Analytics and statistics
  async getCardStats(): Promise<GoatCardsApiResponse<{
    totalCards: number;
    totalValue: number;
    averagePrice: number;
    cardsBySport: { [key: string]: number };
    cardsByYear: { [key: string]: number };
    hotCardsCount: number;
  }>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<any>> = await this.api.get('/cards/stats');
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to get card stats:', error);
      throw error;
    }
  }

  async getPriceHistory(cardId: string, days: number = 30): Promise<GoatCardsApiResponse<{
    cardId: string;
    history: Array<{
      date: string;
      price: number;
      change: number;
    }>;
  }>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<any>> = await this.api.get(`/cards/${cardId}/price-history`, {
        params: { days },
      });
      return response.data;
    } catch (error) {
      console.error(`[GoatCards API] Failed to get price history for card ${cardId}:`, error);
      throw error;
    }
  }

  // Bulk operations
  async bulkCreateCards(cards: GoatCardCreateRequest[]): Promise<GoatCardsApiResponse<GoatCard[]>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<GoatCard[]>> = await this.api.post('/cards/bulk', { cards });
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to bulk create cards:', error);
      throw error;
    }
  }

  async bulkUpdateCards(cards: GoatCardUpdateRequest[]): Promise<GoatCardsApiResponse<GoatCard[]>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<GoatCard[]>> = await this.api.put('/cards/bulk', { cards });
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to bulk update cards:', error);
      throw error;
    }
  }

  // Utility methods
  async getAvailableFilters(): Promise<GoatCardsApiResponse<{
    sports: string[];
    teams: string[];
    brands: string[];
    conditions: string[];
    grades: string[];
    gradingCompanies: string[];
    years: number[];
  }>> {
    try {
      const response: AxiosResponse<GoatCardsApiResponse<any>> = await this.api.get('/cards/filters');
      return response.data;
    } catch (error) {
      console.error('[GoatCards API] Failed to get available filters:', error);
      throw error;
    }
  }

  // Error handling utility
  private handleApiError(error: any): never {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      throw new Error(`API Error ${status}: ${data?.message || data?.error || 'Unknown error'}`);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(`Request failed: ${error.message}`);
    }
  }
}

// Export singleton instance
export const goatCardsApiService = new GoatCardsApiService();

// Export the class for testing or custom instances
export default GoatCardsApiService; 